import {
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { useHistogramToXYColorRules } from './hooks/use-histogram-to-xy-color-rules.js';
import {
  ANNOTATIONS_KEYS,
  transformHistogramAnnotationsToXY,
} from './transformer-annotations-to-xy.js';
import { transformHistogramDataToXYChart } from './transformer-data-to-xy.js';
import {
  mapSelectedSeriesToHistogram,
  mapSelectionDomainToHistogramDomains,
} from './transformer-selection-to-histogram.js';
import { mapHistogramSeriesActionsToXY } from './transformer-series-actions-to-xy.js';
import { mapHistogramThresholdsToXY } from './transformer-thresholds-to-xy.js';
import { buildYAxisProps } from './utils/axis-props-mapping.js';
import { buildHistogramImperativeHandle } from './utils/build-histogram-imperative-handle.js';
import { buildYAxesConfigFromConfig } from './utils/build-y-axes-config.js';
import { defineAxisId } from './utils/define-axis-id.js';
import { deriveXYChartPropsFromConfig } from './utils/derive-xychart-props-from-config.js';
import { formatCSV } from './utils/format-csv.js';
import { downloadCSV } from '../../../core/utils/download.js';
import type { IndicatorsDisplay } from '../../core/components/annotations/components/indicators/types.js';
import { getChartStatesTemplates } from '../../core/components/chart-states/utils/get-chart-states-template.js';
import { CSS_DOWNLOAD_FILENAME } from '../../core/constants.js';
import { useHistogramChartConfig } from '../../histogram/hooks/useHistogramChartConfig.js';
import type { HistogramChartProps } from '../../histogram/types/histogram-props.js';
import type { HistogramChartRef } from '../../histogram/types/histogram-ref.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart, XYChartAnnotations } from '../../xy-chart/XYChartRoot.js';

type AnnotationsKey = (typeof ANNOTATIONS_KEYS)[keyof typeof ANNOTATIONS_KEYS];
const getAnnotationField = <T,>(d: unknown, key: AnnotationsKey): T =>
  (d as Record<string, unknown>)[key] as T;

export const HistogramToXY = forwardRef<
  HistogramChartRef,
  PropsWithChildren<HistogramChartProps>
>((props, forwardedRef) => {
  const {
    data: propsData,
    loading,
    children,
    className,
    style,
    width,
    height,
    colorPalette,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    truncationMode,
    valueRepresentation,
    seriesActions,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  } = props;

  const config = useHistogramChartConfig(children, {
    colorPalette,
    truncationMode,
    valueRepresentation,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });

  const { barSeriesProps, units, seriesColors } =
    transformHistogramDataToXYChart(propsData);

  const allColorRules = useHistogramToXYColorRules({
    data: propsData,
    seriesColors,
    colorRules: config.colorRules,
  });

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const downloadDataCallback = useCallback(() => {
    const csvString = formatCSV(propsData);
    downloadCSV(csvString, CSS_DOWNLOAD_FILENAME);
  }, [propsData]);

  const selectActions = config.select?.actions;
  const mappedSeriesActions = useMemo(
    () =>
      seriesActions
        ? mapHistogramSeriesActionsToXY(seriesActions, propsData)
        : undefined,
    [seriesActions, propsData],
  );

  const yAxesConfig = buildYAxesConfigFromConfig(config);

  const mappedThresholds = useMemo(
    () => mapHistogramThresholdsToXY(config.thresholds, [...units]),
    [config.thresholds, units],
  );

  const {
    xAxisConfig,
    disableZoom,
    disablePan,
    toolbarEnabled,
    toolbarPlacement,
    toolbarIntents,
    onZoomChange: effectiveOnZoomChange,
  } = deriveXYChartPropsFromConfig(config, onZoomChange);

  const xyRef = useRef<XYChartRef>(null);

  useImperativeHandle(
    forwardedRef,
    () => buildHistogramImperativeHandle(xyRef, config),
    [config],
  );

  const annotations = config.annotations;
  const xAxisId = 'x-axis';

  return (
    <XYChart
      data={[]}
      ref={xyRef}
      loading={loading}
      className={className}
      style={style}
      width={width}
      height={height}
      colorPalette={config.colorPalette}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      truncationMode={config.truncationMode}
      __downloadDataCallback={downloadDataCallback}
    >
      {errorState && <XYChart.ErrorState>{errorState}</XYChart.ErrorState>}
      {emptyState && <XYChart.EmptyState>{emptyState}</XYChart.EmptyState>}
      {selectActions ? (
        <XYChart.Select
          actions={(selectedSeries, selectionDomain) =>
            selectActions(
              mapSelectedSeriesToHistogram(selectedSeries),
              mapSelectionDomainToHistogramDomains(selectionDomain),
            )
          }
        />
      ) : null}
      {barSeriesProps.map(({ data, unit }, index) => (
        <XYChart.BarSeries
          key={index}
          data={data}
          xAxisId={xAxisId}
          yAxisId={defineAxisId(unit)}
          x0Accessor="x0"
          x1Accessor="x1"
          y1Accessor="y1"
          variant={
            config.valueRepresentation === 'relative' && propsData.length === 1
              ? 'single'
              : 'stack'
          }
          seriesIdAccessor="seriesId"
          nameAccessor="name"
          actions={mappedSeriesActions}
        />
      ))}
      <XYChart.XAxis
        id={xAxisId}
        type={xAxisConfig.scale === 'log' ? 'log' : 'numerical'}
        position="bottom"
        disableZoom={disableZoom}
        disablePan={disablePan}
        initialZoom={config.initialZoom as [number, number]}
        currentZoom={config.currentZoom as [number, number]}
        onZoomChange={effectiveOnZoomChange}
        infiniteZoom={config.infiniteZoom}
        {...xAxisConfig}
      />
      {[...units].map((unit, index) => (
        <XYChart.YAxis
          key={defineAxisId(unit)}
          {...buildYAxisProps(
            index,
            unit,
            yAxesConfig,
            config.valueRepresentation,
          )}
        />
      ))}
      <XYChart.Legend {...config.legend} />
      {allColorRules.map((rule) => (
        <XYChart.ColorRule
          key={`${rule.comparator}-${rule.matchValue}-${rule.valueAccessor}`}
          {...rule}
        />
      ))}
      {mappedThresholds.map((threshold) => (
        <XYChart.ThresholdIndicator
          key={`${threshold.yAxisId}-${threshold.color}-${
            threshold.label ?? ''
          }`}
          {...threshold}
        />
      ))}
      <XYChart.Toolbar hidden={!toolbarEnabled} placement={toolbarPlacement} />
      {toolbarIntents?.map((intentProps, index) => (
        <XYChart.Intent key={index} {...intentProps} />
      ))}
      {annotations && (
        <XYChart.Annotations
          data={transformHistogramAnnotationsToXY(annotations.tracks)}
          xAxisId={xAxisId}
          showLabels={annotations.showLabels}
          fixedTracks={annotations.visibleTracksLimit}
          annotationsActions={annotations.annotationsActions}
        >
          <XYChartAnnotations.Track
            trackIdAccessor={ANNOTATIONS_KEYS.trackId}
            labelAccessor={ANNOTATIONS_KEYS.trackLabel}
            colorAccessor={ANNOTATIONS_KEYS.trackColor}
            hidden={(d) =>
              getAnnotationField<boolean>(d, ANNOTATIONS_KEYS.trackHidden)
            }
            symbol={(d) =>
              getAnnotationField<ReactNode>(d, ANNOTATIONS_KEYS.trackSymbol)
            }
            indicatorsDisplay={(d) =>
              getAnnotationField<IndicatorsDisplay>(
                d,
                ANNOTATIONS_KEYS.trackIndicators,
              )
            }
          />
          <XYChartAnnotations.Marker
            startAccessor={ANNOTATIONS_KEYS.markerStart}
            endAccessor={ANNOTATIONS_KEYS.markerEnd}
            trackByAccessor={ANNOTATIONS_KEYS.trackId}
            titleAccessor={ANNOTATIONS_KEYS.markerTitle}
            descriptionAccessor={ANNOTATIONS_KEYS.markerDescription}
            colorAccessor={ANNOTATIONS_KEYS.markerColor}
            hidden={(d) =>
              getAnnotationField<boolean>(d, ANNOTATIONS_KEYS.markerHidden)
            }
            symbol={(d) =>
              getAnnotationField<ReactNode>(d, ANNOTATIONS_KEYS.markerSymbol)
            }
            indicatorsDisplay={(d) =>
              getAnnotationField<IndicatorsDisplay>(
                d,
                ANNOTATIONS_KEYS.markerIndicators,
              )
            }
            priority={(d) =>
              getAnnotationField<number>(d, ANNOTATIONS_KEYS.markerPriority)
            }
          />
          {annotations.tooltip?.template && (
            <XYChartAnnotations.Tooltip hidden={annotations.tooltip.hidden}>
              {annotations.tooltip.template}
            </XYChartAnnotations.Tooltip>
          )}
        </XYChart.Annotations>
      )}
    </XYChart>
  );
});

(
  HistogramToXY as typeof HistogramToXY & {
    displayName: string;
  }
).displayName = 'HistogramToXY';
